import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { fmtCurrency, fmtNumber } from "../lib/format";
import { TopBar } from "../components/TopBar";
import { EditableCell } from "../components/EditableCell";
import { PaymentsSection } from "../components/PaymentsSection";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Plus, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";

const BalanceTable = ({ prefix, label, opening, payments, expenses, total, onSave, paymentEntries, onAddPayment, onUpdatePayment }) => (
  <Card className="shadow-sm">
    <CardHeader className="pb-3">
      <CardTitle className="text-base font-semibold">{label} Balance</CardTitle>
      <p className="text-xs text-muted-foreground">
        Expenses include only traffic rows where B = {label}. Payments auto-sums the {label} payment log below.
      </p>
    </CardHeader>
    <CardContent className="space-y-5">
      <div className="w-full overflow-x-auto rounded-md border border-border">
        <Table data-testid={`source-balance-table-${prefix}`} className="min-w-[360px]">
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Reason</TableHead>
              <TableHead className="w-[200px] text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">Opening balance</TableCell>
              <TableCell className="p-1.5">
                <EditableCell
                  value={opening}
                  allowNegative
                  testId={`${prefix}-opening-input`}
                  onSave={(v) => onSave(`${prefix}_opening`, v)}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Payments</TableCell>
              <TableCell
                data-testid={`${prefix}-payments-cell`}
                className={cn(
                  "bg-muted/50 text-right font-mono tabular-nums",
                  payments < 0 ? "text-red-600" : "text-foreground"
                )}
              >
                {fmtCurrency(payments)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Expenses</TableCell>
              <TableCell
                data-testid={`${prefix}-expenses-cell`}
                className={cn(
                  "bg-muted/50 text-right font-mono tabular-nums",
                  expenses < 0 ? "text-red-600" : "text-foreground"
                )}
              >
                {fmtCurrency(expenses)}
              </TableCell>
            </TableRow>
            <TableRow className="border-t-2 border-border bg-accent/30">
              <TableCell className="font-semibold">Total</TableCell>
              <TableCell
                data-testid={`${prefix}-total-cell`}
                className={cn(
                  "text-right font-mono font-semibold tabular-nums",
                  total < 0 ? "text-red-600" : "text-accent-foreground"
                )}
              >
                {fmtCurrency(total)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      <div>
        <p className="mb-2 text-sm font-semibold">{label} Payments — This Week</p>
        <PaymentsSection
          entries={paymentEntries || []}
          total={payments}
          onAdd={onAddPayment}
          onUpdate={onUpdatePayment}
          testPrefix={prefix}
        />
      </div>
    </CardContent>
  </Card>
);

export default function SourceDetailPage() {
  const { sourceId } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingRow, setAddingRow] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/sources/${sourceId}`);
      setDetail(res.data);
    } catch (e) {
      toast.error("Failed to load source");
    } finally {
      setLoading(false);
    }
  }, [sourceId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveRowField = async (rowId, field, value) => {
    try {
      const res = await api.patch(`/sources/${sourceId}/rows/${rowId}`, { [field]: value });
      setDetail(res.data);
    } catch (e) {
      toast.error("Failed to save. Value reverted.");
      load();
    }
  };

  const saveSourceField = async (field, value) => {
    try {
      const res = await api.patch(`/sources/${sourceId}`, { [field]: value });
      setDetail(res.data);
    } catch (e) {
      toast.error("Failed to save. Value reverted.");
      load();
    }
  };

  const addRow = async () => {
    setAddingRow(true);
    try {
      const res = await api.post(`/sources/${sourceId}/rows`);
      setDetail(res.data);
    } catch (e) {
      toast.error("Failed to add row");
    } finally {
      setAddingRow(false);
    }
  };

  const addPayment = (bucket) => async (date, amount, note) => {
    try {
      const res = await api.post(`/sources/${sourceId}/payments`, { bucket, date, amount, note });
      setDetail(res.data);
      toast.success(`${bucket} payment logged`);
    } catch (e) {
      toast.error("Failed to add payment");
    }
  };

  const updatePayment = async (paymentId, field, value) => {
    try {
      const res = await api.patch(`/sources/${sourceId}/payments/${paymentId}`, { [field]: value });
      setDetail(res.data);
    } catch (e) {
      toast.error("Failed to save payment. Value reverted.");
      load();
    }
  };

  const confirmWeek = async () => {
    try {
      const res = await api.post(`/sources/${sourceId}/confirm-week`);
      setDetail(res.data);
      toast.success("Week confirmed — archived and PH/QG Opening Balances carried forward");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to confirm week");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30">
        <TopBar crumbs={[{ label: "Sources", to: "/sources" }]} />
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="min-h-screen bg-muted/30">
        <TopBar crumbs={[{ label: "Sources", to: "/sources" }]} />
        <div className="mx-auto max-w-7xl px-4 py-16 text-center text-muted-foreground">Source not found.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <TopBar crumbs={[{ label: "Sources", to: "/sources" }, { label: detail.name }]} />
      <main className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <h1 data-testid="source-name-heading" className="text-2xl font-semibold tracking-tight sm:text-3xl">
            {detail.name}
          </h1>
          <div className="flex flex-wrap items-center gap-3">
            {detail.week_confirmed ? (
              <div
                data-testid="source-week-confirmed-badge"
                className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700"
              >
                <CheckCircle2 className="h-4 w-4" />
                Week confirmed{detail.confirmed_by ? ` by ${detail.confirmed_by}` : ""}
              </div>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" data-testid="source-confirm-week-button">
                    <CheckCircle2 className="mr-1.5 h-4 w-4" />
                    Confirm calculation
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="source-confirm-week-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirm calculation</AlertDialogTitle>
                    <AlertDialogDescription>
                      Close this source's accounting week now? Its confirmed data will be archived and the final
                      PH and QG Totals become the new Opening Balances. The automatic Tuesday rollover will skip
                      this source. Activity belonging to the new week is kept.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="source-confirm-week-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={confirmWeek} data-testid="source-confirm-week-confirm">
                      Yes, confirm and roll over
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <div className="rounded-lg border border-primary/30 bg-accent/40 px-4 py-2">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">PH Balance</span>
              <span
                data-testid="source-ph-balance-banner"
                className={cn(
                  "ml-3 font-mono text-lg font-semibold tabular-nums",
                  detail.ph_total < 0 ? "text-red-600" : "text-accent-foreground"
                )}
              >
                {fmtCurrency(detail.ph_total)}
              </span>
            </div>
            <div className="rounded-lg border border-primary/30 bg-accent/40 px-4 py-2">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">QG Balance</span>
              <span
                data-testid="source-qg-balance-banner"
                className={cn(
                  "ml-3 font-mono text-lg font-semibold tabular-nums",
                  detail.qg_total < 0 ? "text-red-600" : "text-accent-foreground"
                )}
              >
                {fmtCurrency(detail.qg_total)}
              </span>
            </div>
          </div>
        </div>

        {/* TABLE 1 — TRAFFIC DATA */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Traffic Data</CardTitle>
            <p className="text-xs text-muted-foreground">
              CRG is a manual % input. Gu. FTD's and Expense update automatically. Use column B to assign each row to PH or QG.
            </p>
          </CardHeader>
          <CardContent>
            <div className="w-full overflow-x-auto rounded-md border border-border">
              <Table data-testid="source-traffic-table" className="min-w-[1200px]">
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-[110px]">Geo</TableHead>
                    <TableHead className="w-[120px] text-right">Total leads</TableHead>
                    <TableHead className="w-[120px] text-right">Valid leads</TableHead>
                    <TableHead className="w-[100px] text-right">CRG</TableHead>
                    <TableHead className="w-[110px] text-right">CPA</TableHead>
                    <TableHead className="w-[110px] text-right">Gu. FTD's</TableHead>
                    <TableHead className="w-[110px] text-right">FTD's</TableHead>
                    <TableHead className="w-[130px] text-right">Expense</TableHead>
                    <TableHead className="w-[110px]">B</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="py-8 text-center text-sm text-muted-foreground">
                        No rows yet. Click "+ Add Row" to start entering traffic data.
                      </TableCell>
                    </TableRow>
                  )}
                  {detail.rows.map((row, idx) => (
                    <TableRow key={row.id} className="transition-colors duration-150 hover:bg-muted/40">
                      <TableCell className="p-1.5">
                        <EditableCell
                          type="text"
                          value={row.geo}
                          placeholder="US"
                          testId={`source-row-${idx}-geo-input`}
                          onSave={(v) => saveRowField(row.id, "geo", v)}
                        />
                      </TableCell>
                      <TableCell className="p-1.5">
                        <EditableCell
                          value={row.total_leads}
                          testId={`source-row-${idx}-total-leads-input`}
                          onSave={(v) => saveRowField(row.id, "total_leads", v)}
                        />
                      </TableCell>
                      <TableCell className="p-1.5">
                        <EditableCell
                          value={row.valid_leads}
                          testId={`source-row-${idx}-valid-leads-input`}
                          onSave={(v) => saveRowField(row.id, "valid_leads", v)}
                        />
                      </TableCell>
                      <TableCell className="p-1.5">
                        <EditableCell
                          value={row.crg}
                          suffix="%"
                          placeholder="0"
                          testId={`source-row-${idx}-crg-input`}
                          onSave={(v) => saveRowField(row.id, "crg", v)}
                        />
                      </TableCell>
                      <TableCell className="p-1.5">
                        <EditableCell
                          value={row.cpa}
                          testId={`source-row-${idx}-cpa-input`}
                          onSave={(v) => saveRowField(row.id, "cpa", v)}
                        />
                      </TableCell>
                      <TableCell
                        className="bg-muted/50 text-right font-mono text-sm tabular-nums text-muted-foreground"
                        data-testid={`source-row-${idx}-gu-ftd-cell`}
                      >
                        {fmtNumber(row.gu_ftd)}
                      </TableCell>
                      <TableCell className="p-1.5">
                        <EditableCell
                          value={row.ftd}
                          testId={`source-row-${idx}-ftd-input`}
                          onSave={(v) => saveRowField(row.id, "ftd", v)}
                        />
                      </TableCell>
                      <TableCell
                        className={cn(
                          "bg-muted/50 text-right font-mono text-sm tabular-nums",
                          row.expense < 0 ? "text-red-600" : "text-foreground"
                        )}
                        data-testid={`source-row-${idx}-expense-cell`}
                      >
                        {fmtCurrency(row.expense)}
                      </TableCell>
                      <TableCell className="p-1.5">
                        <Select value={row.b} onValueChange={(v) => saveRowField(row.id, "b", v)}>
                          <SelectTrigger
                            className="h-9 font-medium"
                            aria-label="Balance bucket"
                            data-testid={`source-row-${idx}-b-select`}
                          >
                            <SelectValue placeholder="PH / QG" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PH" data-testid={`source-row-${idx}-b-option-ph`}>PH</SelectItem>
                            <SelectItem value="QG" data-testid={`source-row-${idx}-b-option-qg`}>QG</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Button
              variant="secondary"
              className="mt-3"
              onClick={addRow}
              disabled={addingRow}
              data-testid="source-traffic-add-row-button"
            >
              {addingRow ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}
              Add Row
            </Button>
          </CardContent>
        </Card>

        {/* PH / QG BALANCE TABLES */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <BalanceTable
            prefix="ph"
            label="PH"
            opening={detail.ph_opening}
            payments={detail.ph_payments}
            expenses={detail.ph_expenses}
            total={detail.ph_total}
            onSave={saveSourceField}
            paymentEntries={detail.ph_payment_entries}
            onAddPayment={addPayment("PH")}
            onUpdatePayment={updatePayment}
          />
          <BalanceTable
            prefix="qg"
            label="QG"
            opening={detail.qg_opening}
            payments={detail.qg_payments}
            expenses={detail.qg_expenses}
            total={detail.qg_total}
            onSave={saveSourceField}
            paymentEntries={detail.qg_payment_entries}
            onAddPayment={addPayment("QG")}
            onUpdatePayment={updatePayment}
          />
        </div>
      </main>
    </div>
  );
}
